import { useEffect, useState } from 'react'

import { Card, StyledText } from '@skytech/ui'
import { Button } from '@skytech/ui/Button'
import { Container } from '@skytech/ui/Container'
import { useQuery } from '@tanstack/react-query'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { twJoin } from 'tailwind-merge'

import { Translation } from '@/app/translations/Translation'
import { NoResults } from '@/components/NoResults'
import { useWhiteLabelBasedAccess } from '@/hooks/access'
import type { Feature } from '@/modules/admin/resources'
import adminResources from '@/modules/admin/resources'
import { CustomerFeatureStatus } from '@/modules/admin/resources/features/customerFeatureStatusEnum'

import queries from '../queries'
import type { FeatureOriginal } from '../resources/features'

import { ActivationHistory } from './ActivationHistory'
import { FeatureModal } from './FeatureModal'
import { FeatureSalesPitchModal } from './FeatureSalesPitch'

export const Features = () => {
  const navigate = useNavigate()
  const { hash } = useLocation()
  const [searchParams] = useSearchParams()
  const selectedFeatureIdParam = searchParams.get('selectedFeatureId')
  const selectedCustomerIdParam = searchParams.get('selectedCustomerId')
  const selectedFeatureId = selectedFeatureIdParam ? Number(selectedFeatureIdParam) : null
  const selectedCustomerId = selectedCustomerIdParam ? Number(selectedCustomerIdParam) : null
  // This is used to temporarily disable the feature based on the white label
  useWhiteLabelBasedAccess(['telia'])

  const [openSalesPitchDescription, setOpenSalesPitchDescription] = useState<string | null>(null)
  const [openPopupFeatureId, setOpenPopupFeatureId] = useState<number | null>(selectedFeatureId)
  const [openActivationHistory, setOpenActivationHistory] = useState<FeatureOriginal | null>(null)

  const { data: featuresList, isLoading: isLoadingFeatures } = useQuery(
    queries.adminFeatures(),
    adminResources.getFeatureList
  )
  const { data: customers, isLoading: isLoadingCustomers } = useQuery(
    queries.adminCustomers(),
    adminResources.getCustomers
  )

  useEffect(() => {
    const elementId = hash.split('#')[1]
    document.getElementById(elementId)?.scrollIntoView({ behavior: 'smooth' })
  }, [hash])

  const getActiveCountFromFeature = (customerFeatureList: Feature['customerFeatures']) =>
    customerFeatureList.reduce(
      (active: number, feature) =>
        feature.statusId === CustomerFeatureStatus.Active ? active + 1 : active,
      0
    )

  if (isLoadingFeatures || isLoadingCustomers) return null
  if (!customers) throw Error('We encountered an error while loading customers...')

  const gridCols = (featuresListLength: number): string => {
    const number = featuresListLength > 4 ? 4 : featuresListLength
    return 'lg:grid-cols-' + String(number)
  }

  return (
    <Container onSwipedLeft={() => navigate('/')}>
      <Container.Header>
        <Translation id="Features" />
      </Container.Header>
      {featuresList && featuresList.length > 0 ? (
        <div className={twJoin('grid gap-2 sm:grid-cols-2', gridCols(featuresList.length))}>
          {featuresList?.map(feature => {
            const { id, customerFeatures, salesPitch } = feature

            return (
              <Card key={id} className="flex flex-col justify-between gap-5">
                <div
                  id={feature.name}
                  className="flex flex-grow flex-col justify-between text-start"
                >
                  {salesPitch && (
                    <div>
                      <StyledText size="xl" colorMode="black" className="font-bold">
                        <Translation id={salesPitch.title} className="capitalize" />
                      </StyledText>

                      <div className="mt-3">{salesPitch.description}</div>
                    </div>
                  )}
                  <span className="flex flex-col">
                    <button
                      className="text-primary m-0 w-fit cursor-pointer border-0 bg-white p-0 text-start text-base"
                      onClick={() => setOpenActivationHistory(feature)}
                    >
                      (<Translation id="Activity history" />)
                    </button>
                    {salesPitch && (
                      <button
                        className="text-primary m-0 w-fit cursor-pointer border-0 bg-white p-0 text-start text-base"
                        onClick={() => setOpenSalesPitchDescription(salesPitch.salesPitch)}
                      >
                        (<Translation id="Learn more" />)
                      </button>
                    )}
                  </span>
                </div>

                <Button
                  variant="secondary"
                  className="m-0 w-full"
                  onClick={() => {
                    setOpenPopupFeatureId(id)
                  }}
                >
                  <span>
                    {getActiveCountFromFeature(customerFeatures)} / {customers?.length ?? 0}{' '}
                    <Translation id="activated" />
                  </span>
                </Button>

                {openPopupFeatureId === id && (
                  <FeatureModal
                    selectedCustomerId={selectedCustomerId}
                    customers={customers}
                    feature={feature}
                    onClose={() => setOpenPopupFeatureId(null)}
                  />
                )}
              </Card>
            )
          })}
        </div>
      ) : (
        <NoResults />
      )}
      <FeatureSalesPitchModal
        salesPitch={openSalesPitchDescription ?? ''}
        onClose={() => setOpenSalesPitchDescription(null)}
      />

      {openActivationHistory && (
        <ActivationHistory
          feature={openActivationHistory}
          onClose={() => setOpenActivationHistory(null)}
        />
      )}
    </Container>
  )
}
